import { useEffect } from 'react'
import { useBlocker, type Blocker } from 'react-router-dom'

/**
 * Retenir au départ tant qu'il reste du travail commencé.
 *
 * Observé sur le pilote : le biologiste ouvre un cas, regarde ce qu'IRIS
 * propose, et s'en va sans valider une seule bandelette. Le cas reste à
 * moitié fait, personne ne le reprend, et le dossier perd ce qu'il contenait.
 * La parade n'est pas d'interdire mais de le dire au moment précis où il part.
 *
 * Deux sorties à couvrir, et elles ne se préviennent pas de la même façon :
 *
 *  - la navigation DANS l'application, interceptée par le routeur. C'est la
 *    seule où l'on peut poser sa propre question, avec ses propres mots et le
 *    compte exact de ce qui reste.
 *  - la fermeture de l'onglet ou le rechargement, où le navigateur n'accepte
 *    qu'un dialogue générique dont le texte ne nous appartient pas. On ne peut
 *    que déclencher ce dialogue — le rédiger, non. Autant ne pas essayer.
 *
 * Rend l'état du routeur : `blocked` quand un départ a été retenu, avec
 * `proceed()` pour laisser partir et `reset()` pour rester.
 */
export function useLeaveGuard(
  when: boolean,
  /**
   * Ce qui compte comme un départ. Sans cela le garde retient AUSSI les
   * redirections internes — celle qui suit une analyse, par exemple, pose
   * l'adresse du cas tout juste créé et déclencherait la question à chaque
   * lancement, pour un cas que personne n'a encore eu le temps de valider.
   */
  isLeaving: (args: { currentLocation: { pathname: string }; nextLocation: { pathname: string } }) => boolean,
): Blocker {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => when && isLeaving({ currentLocation, nextLocation }),
  )

  useEffect(() => {
    if (!when) return undefined
    const handler = (event: BeforeUnloadEvent) => {
      // La seule façon normalisée de demander la confirmation : empêcher le
      // défaut et renseigner returnValue. Le texte est ignoré par tous les
      // navigateurs modernes, qui affichent le leur.
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [when])

  // Un départ retenu alors que la condition est retombée (la dernière
  // bandelette vient d'être validée) ne doit pas laisser la question à
  // l'écran : on laisse passer.
  useEffect(() => {
    if (!when && blocker.state === 'blocked') blocker.proceed()
  }, [when, blocker])

  return blocker
}
